import axios from 'axios';

// Créer une instance Axios
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api` || 'https://api.momentum.sayna.dev/api',
  timeout: 0 // Pas de timeout pour gérer les uploads volumineux
});

// Fonction pour convertir un objet en FormData
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertToFormData = (data: any): FormData => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (value instanceof File || value instanceof Blob) {
      // Ajouter les fichiers ou les blobs directement
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      // Ajouter les tableaux sous forme de champs multiples
      value.forEach((item, index) => formData.append(`${key}[${index}]`, item));
    } else if (typeof value === 'object' && value !== null) {
      // Gérer les objets imbriqués en les sérialisant (stringifier)
      formData.append(key, JSON.stringify(value));
    } else {
      // Ajouter les valeurs primitives directement
      formData.append(key, value);
    }
  });
  return formData;
};

// Fonction pour détecter la présence de fichiers dans les données
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const containsFile = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false;

  return Object.values(data).some(
    (value) =>
      value instanceof File ||
      value instanceof Blob ||
      (Array.isArray(value) && value.some((item) => item instanceof File || item instanceof Blob))
  );
};

// Typage de la fonction d'intercepteur de requête
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requestInterceptor = (config: any) => {
  const token = localStorage.getItem('api_token');
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // Convertir les données en FormData si des fichiers sont détectés
  if (config.data) {
    if (config.data instanceof FormData) {
      // Si c'est déjà une FormData, ne rien faire
      config.headers['Content-Type'] = 'multipart/form-data';
    } else if (containsFile(config.data)) {
      // Convertir en FormData uniquement si des fichiers sont détectés
      config.data = convertToFormData(config.data);
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      // Si aucun fichier n'est détecté, gérer comme JSON
      config.headers['Content-Type'] = 'application/json';
    }
  }

  return config;
};

// Typage de la fonction d'intercepteur de réponse
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const responseInterceptor = (response: any) => response;

// Typage de la fonction de gestion des erreurs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorInterceptor = (error: any) => {
  if (error.response) {
    if (error.response.status === 401) {
      localStorage.removeItem('accessToken');

      // Mode invité (guideline Apple 5.1.1(v)). Sans compte, un 401 sur une
      // écriture ne signifie pas « session expirée » mais « cette action demande un
      // compte » : on emmène à la page de connexion, qui porte aussi « Créer mon
      // compte », au lieu d'afficher un message que le visiteur ne peut pas résoudre.
      //
      // Restreint aux écritures, et c'est essentiel : des lectures de fond partent à
      // chaque chargement et répondent 401 sans compte — `data/countries` notamment.
      // Sur toutes les méthodes, l'arrivée sur Découvrir repartait aussitôt au login
      // (régression constatée le 07/10). Les lectures qui demandent un compte sont
      // gardées en amont, par `requireAuth` ou par le bloc `GuestGate`.
      const method = String(error.config?.method ?? 'get').toLowerCase();
      const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
      const isGuest = !localStorage.getItem('api_token');
      const alreadyOnLogin = window.location.pathname.startsWith('/admin/auth');

      if (isGuest && isWrite && !alreadyOnLogin) {
        window.location.href = '/admin/auth/login';
      }
    } else if (error.response.status === 500) {
      console.error('Erreur serveur :', error.response.data);
    }
  } else {
    console.error('Erreur réseau ou délai d’attente dépassé');
  }
  return Promise.reject(error);
};

// Ajouter les intercepteurs configurés
axiosInstance.interceptors.request.use(requestInterceptor, errorInterceptor);
axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

export default axiosInstance;
